import { useSnackbar } from 'notistack'
import { useContext, useState } from 'react'
import { FieldErrors } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { adUnitsAttributesMapper } from 'features/inventory/domain/components/ProductAdUnitsTable/utils'
import { AddEditDomainContext } from 'features/inventory/domain/contexts/AddEditDomainContext'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { mapGraphQLErrors } from 'providers/tanstack-query/utils'
import { floatPercentToDTO } from 'utils/number'
import { Schema } from '../../schema'
import { UPDATE_DOMAIN_PRICE_GENIUS } from './api/mutation'
import { Data, Variables } from './api/types'
import { UseUpdateDomainPriceGenius } from './types'

export const useUpdateDomainPriceGenius = (): UseUpdateDomainPriceGenius => {
  const { t } = useTranslation('features/domain')
  const { enqueueSnackbar } = useSnackbar()
  const { setLoading } = useContext(AddEditDomainContext)
  const [errors, setErrors] = useState<FieldErrors<Schema>>({})

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN_PRICE_GENIUS,
    onCompleted: async data => {
      const {
        updateDomainPriceGenius: { errors },
      } = data

      setLoading(false)

      if (errors && errors.length > 0) {
        setErrors(mapGraphQLErrors(errors))
        enqueueSnackbar(t('form.error.update'), { variant: 'error' })
      } else {
        setErrors({})
        enqueueSnackbar(t('form.success.update'), { variant: 'success' })
      }
    },
  })

  const mapper = (data: Schema): Variables => {
    const { adUnitsAttributes, trafficPercent, ...result } = data

    return {
      ...result,
      adUnitsAttributes: adUnitsAttributesMapper<'priceGeniusEnabled'>(
        adUnitsAttributes,
        'priceGeniusEnabled',
      ),
      trafficPercent: floatPercentToDTO(trafficPercent),
    }
  }

  const updateDomainPriceGenius = (data: Schema): void => {
    setLoading(true)

    mutateAsync({
      input: mapper(data),
    })
  }

  return { errors, updateDomainPriceGenius }
}
